<template>
  <div id="app" class="bg-[#020617] text-white font-sans selection:bg-indigo-500/30">
    
    <!-- 全局粒子星空背景 -->
    <StarParticles />

    <!-- 导航栏（滚动增强） -->
    <nav 
      class="p-4 md:p-6 fixed w-full z-50 transition-all duration-500"
      :class="scrolled 
        ? 'backdrop-blur-xl bg-[#020617]/90 shadow-lg shadow-black/20 border-b border-white/5' 
        : 'backdrop-blur-md bg-black/20 border-b border-white/5'"
    >
      <div class="container mx-auto flex justify-between items-center">
        <div class="flex items-center space-x-2">
          <img src="/logo.svg" alt="App Logo" class="h-8 w-8 transition-transform duration-500" :class="scrolled ? 'scale-90' : 'scale-100'">
          <span class="font-bold tracking-tighter text-lg transition-all duration-500" :class="scrolled ? 'text-base' : 'text-lg'">Star Wander</span>
        </div>
        <div class="flex items-center space-x-6">
          <a href="#features" class="text-xs text-slate-400 hover:text-white transition-colors hidden sm:block tracking-wider uppercase">
            {{ displayedContent.navFeatures }}
          </a>
          <a href="#faq" class="text-xs text-slate-400 hover:text-white transition-colors hidden sm:block tracking-wider uppercase">{{ displayedContent.navFaq }}</a>
          <select v-model="lang" class="bg-white/10 text-white text-sm p-2 rounded-lg border-none focus:ring-2 focus:ring-indigo-500 outline-none cursor-pointer">
            <option v-for="languageKey in Object.keys(translations)" :key="languageKey" :value="languageKey" class="bg-slate-900">
              {{ translations[languageKey]["languageName"] }}
            </option>
          </select>
        </div>
      </div>
    </nav>

    <!-- HERO 首屏 -->
    <header class="starlight-static min-h-screen relative flex items-center justify-center overflow-hidden pt-20">
      <div class="absolute inset-0 bg-gradient-to-b from-transparent via-[#020617]/40 to-[#020617] z-0"></div>

      <div class="container mx-auto flex flex-col items-center justify-center text-center px-6 relative z-10">
        <div class="relative group mb-12 animate-fade-in-down">
          <img src="/logo.svg" alt="App Logo" 
            class="relative h-28 w-28 md:h-36 md:w-36 rounded-full border border-white/10 z-10 bg-black/30 backdrop-blur-sm">
          <div class="absolute -inset-2 bg-indigo-500 rounded-full blur-2xl opacity-40 transition-all duration-700 group-hover:opacity-60 z-0"></div>
          <div class="absolute inset-4 bg-white rounded-full blur-xl opacity-20 z-0"></div>
        </div>

        <h1 class="text-4xl sm:text-4xl lg:text-6xl tracking-tight leading-none mb-6 animate-fade-in-left bg-clip-text text-transparent bg-gradient-to-b from-white to-slate-400">
          {{ displayedContent.heroTitle }}
        </h1>
        
        <p class="max-w-2xl text-lg sm:text-2xl text-slate-400 font-light mb-12 animate-fade-in-right">
          {{ displayedContent.heroSubtitle }}
        </p>

        <div class="flex flex-col sm:flex-row flex-wrap gap-4 justify-center items-center animate-fade-in-up">
          <!-- iOS 下载按钮 -->
          <a href="https://apps.apple.com/app/apple-store/id6752117134" target="_blank" v-if="!isAndroid && !isHarmony"
            class="group inline-flex items-center justify-center bg-white/10 backdrop-blur-md text-white border border-white/20 font-bold py-4 px-10 rounded-full transition-all transform hover:scale-105 hover:bg-white/20">
            <img src="/icons/app_store.png" alt="App Store"
              class="h-6 mr-3">
            {{ displayedContent.downloadAppStore }}
          </a>

          <!-- 非 iOS 环境（Android / HarmonyOS / 桌面端） -->
          <template v-if="!isIOS">
            <!-- 海外系统: Google Play -->
            <a href="https://play.google.com/store/apps/details?id=com.faintmoon.staratlas" target="_blank" v-if="!isCn"
              class="group inline-flex items-center justify-center bg-white/10 backdrop-blur-md text-white border border-white/20 font-bold py-4 px-10 rounded-full transition-all transform hover:scale-105 hover:bg-white/20">
              <img src="/icons/google_play.ico"
                alt="Google Play" class="h-6 mr-3">
              {{ displayedContent.downloadGooglePlay }}
            </a>

            <!-- 中国大陆地区下载链接 -->
            <template v-else>
              <!-- Android 下载（当设备属于 Android 或 桌面端浏览时显示） -->
              <a v-if="isAndroid || !isHarmony" href="/starwander-release-v333-8c2072a8.apk"
                class="group inline-flex items-center justify-center bg-white/10 backdrop-blur-md text-white border border-white/20 font-bold py-4 px-10 rounded-full transition-all transform hover:scale-105 hover:bg-white/20">
                <img src="https://developer.android.google.cn/static/images/logos/android_dt.svg" alt="Download Apk" class="h-6 mr-3">
                {{ displayedContent.downloadApk }}
              </a>

              <!-- 鸿蒙系统（HarmonyOS）下载链接（当设备属于 HarmonyOS 或 桌面端浏览时显示） -->
              <!-- <a v-if="isHarmony || (!isAndroid && !isIOS)" href="https://appgallery.huawei.com/#/app/C101234567" target="_blank"
                class="group inline-flex items-center justify-center bg-white/10 backdrop-blur-md text-white border border-white/20 font-bold py-4 px-10 rounded-full transition-all transform hover:scale-105 hover:bg-white/20">
                <img src="/icons/huawei.png"
                alt="App Gallery" class="h-6 mr-3">
                {{ displayedContent.downloadHarmony }}
              </a> -->
              <!-- 鸿蒙系统（HarmonyOS）下载链接（未上架置灰状态） -->
              <div v-if="isHarmony || (!isAndroid && !isIOS)"
                class="group inline-flex items-center justify-center bg-white/5 backdrop-blur-md text-slate-400 border border-white/10 font-bold py-4 px-10 rounded-full opacity-60 cursor-not-allowed select-none">
                <img src="/icons/huawei.png" alt="App Gallery" class="h-6 mr-3 grayscale opacity-60">
                <span>{{ displayedContent.downloadHarmony }}</span>
              </div>
            </template>
          </template>
        </div>
      </div>
      
      <div class="absolute bottom-10 left-1/2 -translate-x-1/2 opacity-30 animate-bounce">
        <div class="w-px h-16 bg-gradient-to-b from-white to-transparent"></div>
      </div>
    </header>

    <!-- 核心功能 -->
    <FeaturesGrid :lang="lang" />

    <!-- FAQ -->
    <FaqSection :lang="lang" id="faq" />

    <!-- Footer -->
    <footer class="bg-black/40 border-t border-white/5 text-slate-500 py-16 text-center relative z-10">
      <div class="container mx-auto px-6">
        <div class="flex flex-wrap justify-center gap-y-4 gap-x-8 text-sm font-medium">
          <!-- iOS 端专有条款 -->
          <template v-if="isIOS">
            <a :href="lang == 'en' ? '/ios/terms-of-service.html' : '/ios/' + lang + '/terms-of-service.html'" class="hover:text-white transition-colors">{{ displayedContent.userAgreement }}</a>
            <a :href="lang == 'en' ? '/ios/privacy-policy.html' : '/ios/' + lang + '/privacy-policy.html'" class="hover:text-white transition-colors">{{ displayedContent.privacyPolicy }}</a>
          </template>

          <!-- Android 端专有条款 -->
          <template v-else-if="isAndroid">
            <a :href="(isCn ? '/cn' : '') + '/android/terms-of-service.html'" class="hover:text-white transition-colors">{{ displayedContent.userAgreement }}</a>
            <a :href="(isCn ? '/cn' : '') + '/android/privacy-policy.html'" class="hover:text-white transition-colors">{{ displayedContent.privacyPolicy }}</a>
          </template>

          <!-- HarmonyOS 端专有条款 -->
          <template v-else-if="isHarmony">
            <a :href="lang == 'en' ? '/harmony/terms-of-service.html' : '/harmony/' + lang + '/terms-of-service.html'" class="hover:text-white transition-colors">{{ displayedContent.userAgreement }}</a>
            <a :href="lang == 'en' ? '/harmony/privacy-policy.html' : '/harmony/' + lang + '/privacy-policy.html'" class="hover:text-white transition-colors">{{ displayedContent.privacyPolicy }}</a>
          </template>

          <!-- 桌面端或常规多端展示 -->
          <template v-else>
            <a :href="lang == 'en' ? '/ios/terms-of-service.html' : '/ios/' + lang + '/terms-of-service.html'" class="hover:text-white transition-colors">
              {{ displayedContent.userAgreement }}{{ displayedContent.iOSSuffix || ' (iOS)' }}
            </a>
            <a :href="lang == 'en' ? '/ios/privacy-policy.html' : '/ios/' + lang + '/privacy-policy.html'" class="hover:text-white transition-colors">
              {{ displayedContent.privacyPolicy }}{{ displayedContent.iOSSuffix || ' (iOS)' }}
            </a>
            <a :href="(isCn ? '/cn' : '') + '/android/terms-of-service.html'" class="hover:text-white transition-colors">
              {{ displayedContent.userAgreement }}{{ displayedContent.androidSuffix || ' (Android)' }}
            </a>
            <a :href="(isCn ? '/cn' : '') + '/android/privacy-policy.html'" class="hover:text-white transition-colors">
              {{ displayedContent.privacyPolicy }}{{ displayedContent.androidSuffix || ' (Android)' }}
            </a>
            <a :href="lang == 'en' ? '/harmony/terms-of-service.html' : '/harmony/' + lang + '/terms-of-service.html'" class="hover:text-white transition-colors">
              {{ displayedContent.userAgreement }} (Harmony OS)
            </a>
            <a :href="lang == 'en' ? '/harmony/privacy-policy.html' : '/harmony/' + lang + '/privacy-policy.html'" class="hover:text-white transition-colors">
              {{ displayedContent.privacyPolicy }} (Harmony OS)
            </a>
          </template>

          <template v-if="isCn">
            <a href="star-bti">
              Star-BTI 测试
            </a>
          </template>
        </div>
        
        <div class="mt-10 flex flex-col items-center space-y-4">
          <a v-if="isCn" href="https://beian.miit.gov.cn" target="_blank" class="text-xs hover:text-indigo-400 transition-colors">
            沪ICP备2022023047号
          </a>
          <p class="text-[10px] tracking-widest uppercase opacity-40">© 2026 Star Wander - Built for Stargazers</p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { translations } from './const'
import StarParticles from './components/StarParticles.vue'
import FeaturesGrid from './components/FeaturesGrid.vue'
import FaqSection from './components/FaqSection.vue'
import { useScrollReveal } from './composables/useScrollReveal'

// 初始化滚动观察器
useScrollReveal()

const isCn = computed(() => window.location.search.includes('country=CN'))
const userAgent = ref("");

const scrolled = ref(false)

let scrollHandler = null

onMounted(() => {
  userAgent.value = navigator.userAgent || navigator.vendor || window.opera;
  document.title = displayedContent.value.title;
  document.documentElement.lang = lang.value;

  // 监听滚动以改变导航栏样式
  scrollHandler = () => {
    scrolled.value = window.scrollY > 60
  }
  window.addEventListener('scroll', scrollHandler, { passive: true })
})

onUnmounted(() => {
  if (scrollHandler) {
    window.removeEventListener('scroll', scrollHandler)
  }
})

const isIOS = computed(() => /iPad|iPhone|iPod/.test(userAgent.value) && !window.MSStream);
const isAndroid = computed(() => /Android/.test(userAgent.value) && !/HarmonyOS|HMOS/.test(userAgent.value));
// 增加对 HarmonyOS / OpenHarmony 的系统识别
const isHarmony = computed(() => /HarmonyOS|HMOS|OpenHarmony/.test(userAgent.value));

const lang = ref(
  Object.keys(translations).includes(localStorage.getItem('lang')) 
  ? localStorage.getItem('lang') 
  : (isCn.value ? "zh-cn" : "en")
);

const displayedContent = computed(() => translations[lang.value]);
</script>

<style scoped>
html {
  scroll-behavior: smooth;
}

.starlight-static {
  background-image: url('https://cdn.pixabay.com/photo/2023/12/28/15/46/ai-generated-8474405_1280.jpg');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  animation: slow-pan 60s infinite alternate linear;
}

/* 滚动触发动画基类 */
:deep(.reveal-on-scroll) {
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 0.8s cubic-bezier(0.22, 1, 0.36, 1), transform 0.8s cubic-bezier(0.22, 1, 0.36, 1);
}

:deep(.reveal-on-scroll.revealed) {
  opacity: 1;
  transform: translateY(0);
}

/* 增强的高级动画 */
@keyframes slow-pan {
  from { background-position: center top; }
  to { background-position: center bottom; }
}

@keyframes fade-in-down {
  from { opacity: 0; transform: translateY(-30px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes fade-in-up {
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes fade-in-left {
  from { opacity: 0; transform: translateX(-40px); }
  to { opacity: 1; transform: translateX(0); }
}

@keyframes fade-in-right {
  from { opacity: 0; transform: translateX(40px); }
  to { opacity: 1; transform: translateX(0); }
}

.animate-fade-in-down { animation: fade-in-down 1.2s cubic-bezier(0.22, 1, 0.36, 1) forwards; }
.animate-fade-in-up { animation: fade-in-up 1.2s cubic-bezier(0.22, 1, 0.36, 1) 0.3s forwards; opacity: 0; }
.animate-fade-in-left { animation: fade-in-left 1.2s cubic-bezier(0.22, 1, 0.36, 1) forwards; }
.animate-fade-in-right { animation: fade-in-right 1.2s cubic-bezier(0.22, 1, 0.36, 1) forwards; }

/* 响应式调整 */
@media (max-width: 640px) {
  .starlight-static {
    background-attachment: scroll;
  }
}
</style>